'use client';

import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

import ChatPanel from './ChatPanel';
import ParticipantsList from './ParticipantsList';
import WhiteboardPanel from './WhiteboardPanel';
import ClassControls from './ClassControls';
import ClassInfo from './ClassInfo';

interface Message {
  id: number;
  sender: string;
  message: string;
  timestamp: string;
  isInstructor: boolean;
  isPrivate?: boolean;
}

interface Participant {
  id: number;
  name: string;
  image: string;
  alt: string;
  role: 'instructor' | 'student';
  isOnline: boolean;
  isMuted: boolean;
  isVideoOn: boolean;
}

const LiveClassroomInteractive = () => {
  const { user } = useAuth();
  const [showWhiteboard, setShowWhiteboard] = useState(false);
  const [showBreakout, setShowBreakout] = useState(false);
  const [activeTab, setActiveTab] = useState<'chat' | 'participants'>('chat');
  const [zoomUrl, setZoomUrl] = useState<string | null>(null);
  const [courseName, setCourseName] = useState('Live Class Session');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.id) {
      fetchEnrolledBatchInfo();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchEnrolledBatchInfo = async () => {
    try {
      setLoading(true);
      
      // Fetch user's active enrollment with batch and course details
      const { data: enrollments, error: enrollError } = await supabase
        .from('course_enrollments')
        .select(`
          id,
          batch_id,
          course_batches!inner(
            id,
            batch_name,
            is_active,
            start_date,
            end_date,
            schedule_info
          ),
          courses!inner(
            title
          )
        `)
        .eq('user_id', user?.id)
        .eq('status', 'active')
        .eq('course_batches.is_active', true)
        .lte('course_batches.start_date', new Date().toISOString())
        .gte('course_batches.end_date', new Date().toISOString())
        .order('enrolled_at', { ascending: false })
        .limit(1);

      if (enrollError) {
        throw enrollError;
      }

      if (enrollments && enrollments.length > 0) {
        const enrollment = enrollments[0];
        setCourseName(enrollment.courses?.title || 'Live Class Session');
        
        // Check if schedule_info contains a Zoom URL
        const scheduleInfo = enrollment.course_batches?.schedule_info;
        if (scheduleInfo && typeof scheduleInfo === 'object' && 'zoom_url' in scheduleInfo) {
          setZoomUrl(scheduleInfo.zoom_url);
        } else {
          setError('Live class link not available yet. Please check back later.');
        }
      } else {
        setError('No active live class found for your enrollment.');
      }
    } catch (err: any) {
      console.error('Error fetching batch info:', err);
      setError(err?.message || 'Failed to load live class details.');
    } finally {
      setLoading(false);
    }
  };

  const mockMessages: Message[] = [
  {
    id: 1,
    sender: 'Dr. Sarah Mitchell',
    message: 'Welcome everyone! Today we will cover advanced React patterns and best practices.',
    timestamp: '10:00 AM',
    isInstructor: true
  },
  {
    id: 2,
    sender: 'Alex Johnson',
    message: 'Good morning! Excited to learn about React patterns.',
    timestamp: '10:01 AM',
    isInstructor: false
  },
  {
    id: 3,
    sender: 'Maria Garcia',
    message: 'Can we discuss custom hooks in detail?',
    timestamp: '10:02 AM',
    isInstructor: false
  },
  {
    id: 4,
    sender: 'Dr. Sarah Mitchell',
    message: 'Absolutely! We will have a dedicated section on custom hooks with practical examples.',
    timestamp: '10:03 AM',
    isInstructor: true
  },
  {
    id: 5,
    sender: 'James Chen',
    message: 'Looking forward to the whiteboard session!',
    timestamp: '10:04 AM',
    isInstructor: false
  }];


  const mockParticipants: Participant[] = [
  {
    id: 1,
    name: 'Dr. Sarah Mitchell',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ffde516e-1763293580273.png",
    alt: 'Professional woman with brown hair in navy blazer smiling at camera in modern office',
    role: 'instructor',
    isOnline: true,
    isMuted: false,
    isVideoOn: true
  },
  {
    id: 2,
    name: 'Alex Johnson',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a9730219-1763294180825.png",
    alt: 'Young man with short brown hair in casual blue shirt smiling outdoors',
    role: 'student',
    isOnline: true,
    isMuted: true,
    isVideoOn: true
  },
  {
    id: 3,
    name: 'Maria Garcia',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_19dc372df-1763294269106.png",
    alt: 'Hispanic woman with long dark hair in professional attire in office setting',
    role: 'student',
    isOnline: true,
    isMuted: true,
    isVideoOn: false
  },
  {
    id: 4,
    name: 'James Chen',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_125809ed7-1763299658253.png",
    alt: 'Asian man with glasses in white shirt working at desk with laptop',
    role: 'student',
    isOnline: true,
    isMuted: false,
    isVideoOn: true
  },
  {
    id: 5,
    name: 'Emily Rodriguez',
    image: "https://images.unsplash.com/photo-1533656812321-1868a4d2b72a",
    alt: 'Young woman with blonde hair in casual attire smiling at camera outdoors',
    role: 'student',
    isOnline: true,
    isMuted: true,
    isVideoOn: true
  },
  {
    id: 6,
    name: 'David Kim',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_13f854515-1763293689585.png",
    alt: 'Professional man in dark suit with tie in corporate office environment',
    role: 'student',
    isOnline: true,
    isMuted: true,
    isVideoOn: false
  },
  {
    id: 7,
    name: 'Sophie Anderson',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d57bef5a-1767754984979.png",
    alt: 'Woman with red hair in casual sweater smiling in bright indoor setting',
    role: 'student',
    isOnline: false,
    isMuted: true,
    isVideoOn: false
  },
  {
    id: 8,
    name: 'Michael Brown',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f08f4a30-1763296200964.png",
    alt: 'Man with beard in casual shirt outdoors with natural background',
    role: 'student',
    isOnline: true,
    isMuted: true,
    isVideoOn: true
  }];


  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-20 pb-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background pt-20 pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-16">
          <h2 className="text-2xl font-bold text-foreground mb-4">Sign In Required</h2>
          <p className="text-muted-foreground mb-6">
            Please sign in to access your live classroom sessions.
          </p>
          <a
            href="/auth/signin"
            className="inline-block px-6 py-3 bg-primary text-white font-semibold rounded-md hover:bg-primary/90 transition">
            Sign In
          </a>
        </div>
      </div>
    );
  }

  if (error || !zoomUrl) {
    return (
      <div className="min-h-screen bg-background pt-20 pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-16">
          <h2 className="text-2xl font-bold text-foreground mb-4">
            {error || 'No Live Class Available'}
          </h2>
          <p className="text-muted-foreground mb-6">
            {error ? 
              error : 'You don\'t have any active live classes scheduled at the moment.'}
          </p>
          <a
            href="/course-catalog"
            className="inline-block px-6 py-3 bg-primary text-white font-semibold rounded-md hover:bg-primary/90 transition">
            Browse Courses
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ClassInfo
          courseName={courseName}
          instructorName="Dr. Sarah Mitchell"
          topic="Live Session"
          duration="2 hours"
          attendees={mockParticipants.length} />


        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            {/* Zoom Meeting Embed */}
            <div className="bg-card rounded-lg border border-border overflow-hidden">
              <div className="aspect-video relative">
                <iframe
                  src={zoomUrl}
                  className="absolute inset-0 w-full h-full"
                  allow="camera; microphone; fullscreen; display-capture"
                  title="Zoom Live Class"
                />
              </div>
              <div className="p-4 bg-card border-t border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium text-foreground">Live Now</span>
                  </div>
                  <a
                    href={zoomUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-primary text-white text-sm font-semibold rounded-md hover:bg-primary/90 transition">
                    Open in Zoom App
                  </a>
                </div>
              </div>
            </div>

            {showWhiteboard &&
            <div className="h-[600px]">
                <WhiteboardPanel isVisible={showWhiteboard} />
              </div>
            }
          </div>

          <div className="space-y-4">
            <div className="bg-card rounded-lg border border-border overflow-hidden">
              <div className="flex border-b border-border">
                <button
                  onClick={() => setActiveTab('chat')}
                  className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  activeTab === 'chat' ? 'bg-primary text-white' : 'bg-card text-muted-foreground hover:bg-muted'}`
                  }>

                  Chat
                </button>
                <button
                  onClick={() => setActiveTab('participants')}
                  className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  activeTab === 'participants' ? 'bg-primary text-white' : 'bg-card text-muted-foreground hover:bg-muted'}`
                  }>

                  Participants
                </button>
              </div>

              <div className="h-[600px]">
                {activeTab === 'chat' ?
                <ChatPanel messages={mockMessages} /> :

                <ParticipantsList participants={mockParticipants} />
                }
              </div>
            </div>
          </div>
        </div>
      </div>

      <ClassControls
        onToggleWhiteboard={() => setShowWhiteboard(!showWhiteboard)}
        onToggleBreakout={() => setShowBreakout(!showBreakout)} />

    </div>);

};

export default LiveClassroomInteractive;